export type ReadingTheme = "paper" | "sepia" | "dark";
export type ReadingFont = "literata" | "inter" | "mono";
export type ReadingLeading = "compact" | "normal" | "relaxed";

export type ReadingPrefs = {
  theme: ReadingTheme;
  font: ReadingFont;
  /** Body font size in px. */
  fontSize: number;
  leading: ReadingLeading;
  /** Screen warmth, 0–100. */
  warmth: number;
};

export const READING_PREFS_KEY = "note-reading-prefs";

export const MIN_FONT_SIZE = 15;
export const MAX_FONT_SIZE = 22;

export const DEFAULT_PREFS: ReadingPrefs = {
  theme: "paper",
  font: "literata",
  fontSize: 17,
  leading: "normal",
  warmth: 0,
};

const THEMES: readonly ReadingTheme[] = ["paper", "sepia", "dark"];
const FONTS: readonly ReadingFont[] = ["literata", "inter", "mono"];
const LEADINGS: readonly ReadingLeading[] = ["compact", "normal", "relaxed"];

function pick<T>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

export function clampFontSize(value: unknown): number {
  const n = typeof value === "number" ? Math.round(value) : NaN;
  if (Number.isNaN(n)) return DEFAULT_PREFS.fontSize;
  return Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, n));
}

function clampWarmth(value: unknown): number {
  const n = typeof value === "number" ? Math.round(value) : NaN;
  if (Number.isNaN(n)) return DEFAULT_PREFS.warmth;
  return Math.min(100, Math.max(0, n));
}

/** localStorage is user-editable, so every field is validated on the way in. */
export function loadPrefs(): ReadingPrefs {
  try {
    const raw = window.localStorage.getItem(READING_PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return DEFAULT_PREFS;
    const p = parsed as Record<string, unknown>;
    return {
      theme: pick(p.theme, THEMES, DEFAULT_PREFS.theme),
      font: pick(p.font, FONTS, DEFAULT_PREFS.font),
      fontSize: clampFontSize(p.fontSize),
      leading: pick(p.leading, LEADINGS, DEFAULT_PREFS.leading),
      warmth: clampWarmth(p.warmth),
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function savePrefs(prefs: ReadingPrefs): void {
  try {
    window.localStorage.setItem(
      READING_PREFS_KEY,
      JSON.stringify({ v: 1, ...prefs }),
    );
  } catch {
    /* private mode / quota — reading mode still works for the session */
  }
}
